import { lazy, type ComponentType } from 'react';

type LazyModule<T> = { default: T };

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

const CHUNK_RELOAD_KEY = 'myu-chunk-reload';

/**
 * Lazy route import with a one-time full reload when Vite HMR serves a stale chunk
 * (common after files are added/removed while the dev server is running).
 */
export function lazyImport<T extends ComponentType<unknown>>(
  importer: () => Promise<LazyModule<T>>,
) {
  return lazy(() =>
    importer().catch((error: unknown) => {
      if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return new Promise<LazyModule<T>>(() => {});
      }
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw error;
    }),
  );
}

export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}
